import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	deployActivePriorityLists,
	fetchPlaylists,
	selectDeploying,
} from "./slice";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import Label from "../../ui/Label";
import Select from "../../ui/Select";
import FormGroup from "../../ui/FormGroup";
import Button from "../../ui/Button";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ErrorMsg from "../../ui/ErrorMsg";
import PropTypes from "prop-types";
import { selectAstrositesById } from "../astrosites/slice";
import Paper from "../../ui/Paper";
import PaperTitle, { TitleText } from "../../ui/PaperTitle";
import PriorityListTable from "../priorities/PriortyListTable";
import {
	fetchPriorityLists,
	selectPriorityListsByContext,
	selectPriorityListsByIds,
} from "../priorities/slice";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import FormError from "../../ui/FormError";

const RadioTip = styled.span`
  font-size: 0.8em;
  padding: 0.5rem 0;
  opacity: 0.8;
`;

const ScheduleForm = styled.div`
	padding: 1rem;
`;

const FormActions = styled.div`
  padding-top: 1rem;
  padding-bottom: 1rem;
  text-align: center;
`;
/**
 * The form validator
 */
const schema = yup
	.object({
		astrosite: yup.string().max(30).required("please select an Astrosite"),
		deploy_to_astrosite: yup
			.string()
			.oneOf(["yes", "no"])
			.required("Please choose either yes or no"),
	})
	.required();

/**
 * Generate tasks for active/selected priority lists
 *
 * @param selectedPriorityListIDs the lists to deploy, if none then will select active lists
 * @returns {JSX.Element}
 * @constructor
 */
const GenerateSchedule = ({ selectedPriorityListIDs }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const generating = useSelector(selectDeploying);
	const astrositesByIds = useSelector(selectAstrositesById);
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			// this is a hack to default to a value (otherwise the wrong behaviour happens.  In future its not a good idea to have form state managed separately from redux state)
			astrosite: "astrosite1",
		},
	});

	// we watch this form field and get new results on chnage
	const activeAstrosite = watch("astrosite");

	// We either find lists by ID or if there are none specified then we select the active ones for the Astrosite in question
	const filter = {};

	// get the lists
	useEffect(() => {
		if (selectedPriorityListIDs?.length > 0) {
			filter.priority_ids = selectedPriorityListIDs;
		} else {
			filter.priority_ids = undefined;
			filter.active_for_astrosite = activeAstrosite;
		}

		if (filter.active_for_astrosite || filter.priority_ids?.length > 0) {
			dispatch(
				fetchPriorityLists({
					filter,
					context: ["schedule"],
				}),
			);
		}
	}, [selectedPriorityListIDs, activeAstrosite]);

	// we get any matching priority lists
	const priorityMatches = useSelector((state) =>
		selectPriorityListsByContext(state, "schedule"),
	);
	const priorities = useSelector((state) =>
		selectPriorityListsByIds(state, priorityMatches?.ids || []),
	);

	const onSubmit = (data) => {
		if (data.deploy_to_astrosite === "yes") {
			data.deploy_to_astrosite = true;
		} else {
			data.deploy_to_astrosite = false;
		}
		dispatch(
			deployActivePriorityLists({
				...data,
				priority_list_ids: priorities.map((p) => p.id),
			}),
		)
			.unwrap()
			.then((p) => {
				// if we have a playlist_id route to the lists page
				if (p?.playlist_id) {
					navigate(`/playlists/${p?.playlist_id}`);
				} else {
					dispatch(
						fetchPlaylists({
							filter: {},
							page: { offset: 0, limit: 10 },
							context: ["root"],
						}),
					);
				}
			});
	};

	return (
		<Paper>
			<PaperTitle>
				<TitleText> Generate observation schedule</TitleText>
			</PaperTitle>
			{generating?._saveStatus === "updating" && (
				<Backdrop>
					<LoadingSpinner />
				</Backdrop>
			)}
			{generating?._saveStatus === "rejected" && (
				<ErrorMsg
					msg={
						generating._errorMessage ||
						"an error occurred generating tasks from priorities"
					}
					showIcon={true}
				/>
			)}
			<ScheduleForm>
				<form onSubmit={handleSubmit(onSubmit)}>
					<FormGroup>
						<Label htmlFor="astrosite">For Astrosite</Label>
						<Select {...register("astrosite")}>
							{Object.values(astrositesByIds)
								.filter((a) => a.enabled === true)
								.map((site) => (
									<option key={site?.id} value={site?.id}>
										{site?.name}
									</option>
								))}
						</Select>
						<FormError>{errors.astrosite?.message}</FormError>
					</FormGroup>
					<FormGroup>
						<Label>Send and apply to Astrosite</Label>
						<div>
							<label>
								<input
									type="radio"
									id="falseOption"
									name="deploy_to_astrosite"
									value="yes"
									{...register("deploy_to_astrosite")}
								/>
								<strong>yes</strong>
								<RadioTip> - replaces any current schedule</RadioTip>
							</label>
						</div>
						<div>
							<label>
								<input
									type="radio"
									id="trueOption"
									name="deploy_to_astrosite"
									value="no"
									{...register("deploy_to_astrosite")}
								/>
								<strong>no</strong>
								<RadioTip> - saves it for later</RadioTip>
							</label>
						</div>
						<FormError>{errors.deploy_to_astrosite?.message}</FormError>
					</FormGroup>
					<FormActions>
						<Button
							type="submit"
							disabled={priorityMatches?._loadStatus === "loading"}
							new
						>
							Submit
						</Button>
					</FormActions>
				</form>
			</ScheduleForm>
			<Paper>
				<PaperTitle>
					<TitleText loadingState={priorityMatches?._loadStatus || "idle"}>
						{priorityMatches?._loadStatus !== "loading" ? (
							<div>
								{" "}
								{priorities?.length || 0}{" "}
								{selectedPriorityListIDs?.length > 0 ? "selected" : "active"}{" "}
								priority lists
							</div>
						) : (
							<span>loading priority lists</span>
						)}
					</TitleText>
				</PaperTitle>
				{priorityMatches?._loadStatus !== "loading" && (
					<div>
						{" "}
						<PriorityListTable
							priorities={priorities}
							hideFilters={true}
							hideControls={true}
						/>
						{priorities?.length === 0 &&
							priorityMatches?._loadStatus === "idle" && (
								<p>There are no priority lists matching your criteria.</p>
							)}
					</div>
				)}
			</Paper>
		</Paper>
	);
};

GenerateSchedule.propTypes = {
	// Optionally indicates specific lists. Could be null in which case active lists are selected
	selectedPriorityListIDs: PropTypes.array,
};
export default GenerateSchedule;
